/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { Root } from 'hast';
import { renderToStaticMarkup } from 'react-dom/server';
import rehypeParse from 'rehype-parse';
import rehypeStringify from 'rehype-stringify';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';
import { CKEditor5Preview } from '../../components/CKEditor5Preview/CKEditor5Preview';
import {
	EDITOR_SOURCE_IDENTIFIER
} from '../../constants/identifiers';
import { rehypePluginCleanTree } from '../rehypePluginCleanTree/rehypePluginCleanTree';
import { getAngularRef } from '../getAngularRef/getAngularRef';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';
import type { EditorType } from '@ckeditor/ckeditor5-metadata';
import type { InstallMethod } from '../../constants/installation';
import type { GetCodeHtmlOptions } from '../getProjectFiles/types';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { requiresWordCountPlugin } from '../requiresWordCountPlugin/requiresWordCountPlugin';

export function getCodeAngularHtml( {
	editorOptions,
	selectedInstallMethod
}: GetCodeHtmlOptions ): string {
	const { editorType, toolbarOptions, editorPlugins } = editorOptions;

	const staticMarkup = renderToStaticMarkup(
		<CKEditor5Preview
			locationOrigin=''
			editorPlugins={ editorPlugins }
			editorType={ editorType }
			toolbarOptions={ toolbarOptions }
		/>
	);

	return unified()
		.use( rehypeParse, { fragment: true } )
		.use( rehypePluginAttachAngularComponent )
		.use( rehypePluginAttachRefs )
		.use( rehypePluginCleanTree )
		.use( rehypeStringify )
		.processSync( staticMarkup )
		.toString()
		.replaceAll( /"?__UNQUOTE__"?/g, '' )
		.replaceAll( /ref={\s?(\w+)\s?}/g, '#$1' )
		.replace( '__ANGULAR_COMPONENT_PLACEHOLDER__', getAngularComponent( editorType, selectedInstallMethod, editorPlugins ) )
		.trim();
}

function rehypePluginAttachAngularComponent() {
	return ( tree: Root ) => {
		visit( tree, 'element', node => {
			if ( node.properties.dataSnippetSelector === EDITOR_SOURCE_IDENTIFIER ) {
				node.children = [ {
					type: 'text',
					value: '__ANGULAR_COMPONENT_PLACEHOLDER__'
				} ];

				delete node.properties.dataSkipChildrenInSnippets;
			}
		} );
	};
}

function rehypePluginAttachRefs() {
	return ( tree: Root ) => {
		visit( tree, 'element', node => {
			if ( node.properties.dataSnippetSelector ) {
				const snippetSelector = node.properties.dataSnippetSelector as string;
				const { htmlRefElement } = getAngularRef( snippetSelector );

				node.properties.ref = `__UNQUOTE__{ ${ htmlRefElement } }__UNQUOTE__`;
			}
		} );
	};
}

function getAngularComponent( editorType: EditorType, selectedInstallMethod: InstallMethod, editorPlugins: EditorPlugins ) {
	return `
		<ckeditor 
			[editor]="Editor"
			[config]="config"
			${ isDecoupledEditor( editorType ) || requiresWordCountPlugin( editorPlugins ) ? '(ready)="onReady($event)"' : '' }
			${ selectedInstallMethod === 'npm' ? '*ngIf="isLayoutReady"' : '*ngIf="Editor && config"' }
		/>
	`;
}
